/* ============================================================
   KE PDP image zoom — magnifier button + fullscreen lightbox.
   v2 after adversarial review (2026-08-23): touch layer is a
   real state machine (pinch->pan re-baselining, touchcancel,
   ghost-touch purge), tap handling is native-touch (no
   synthesized-click zoom collapse / double-tap self-cancel),
   loading + error states, capture-phase keys so Esc/Tab never
   leak to ke-pdp.js or overlays, keyboard zoom, inert
   background, shopify:section:load re-init.

   Self-contained on purpose: reads the gallery DOM that
   ke-main-product.liquid renders (#keMainImg, .ke-thumbs .th)
   and never touches ke-pdp.js.
   ============================================================ */
(function () {
  'use strict';

  var HIRES = 2048;   // Shopify CDN width for the lightbox image
  var ZOOM = 2.6;     // tap/click zoom scale
  var MAXZ = 5;
  var TAP_SLOP = 12;      // px of movement that still counts as a tap
  var TAP_DEBOUNCE = 350; // ms — a double-tap acts once

  var COARSE = false;
  try { COARSE = window.matchMedia('(pointer: coarse)').matches; } catch (e) { /* ignore */ }

  /* ---------- gallery model ---------- */
  function mainImgEl() { return document.getElementById('keMainImg'); }

  function thumbs() {
    return Array.prototype.slice.call(document.querySelectorAll('.ke-thumbs .th'));
  }
  function currentMainSrc() {
    var m = mainImgEl();
    return m ? (m.currentSrc || m.getAttribute('src') || '') : '';
  }
  /* compare CDN urls by path, ignoring ?width/&v params */
  function samePath(a, b) {
    return a && b && a.split('?')[0] === b.split('?')[0];
  }
  function slides() {
    var m = mainImgEl();
    var list = thumbs().map(function (t) {
      return { url: t.dataset.full || '', alt: t.dataset.alt || (m ? m.alt : '') || '' };
    }).filter(function (s) { return s.url; });
    var cur = currentMainSrc();
    /* the on-screen image can be a variant image beyond the 5-thumb limit —
       it must always be in the list, and first when missing */
    if (cur && !list.some(function (s) { return samePath(s.url, cur); })) {
      list.unshift({ url: cur, alt: m ? m.alt : '' });
    }
    if (!list.length && cur) list = [{ url: cur, alt: m ? m.alt : '' }];
    return list;
  }
  function startIndex(list) {
    var cur = currentMainSrc();
    for (var j = 0; j < list.length; j++) if (samePath(list[j].url, cur)) return j;
    return 0;
  }
  /* Shopify CDN urls carry ?width=NNN — swap it up for real detail. */
  function hires(url) {
    if (!url) return url;
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + HIRES);
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + HIRES;
  }

  /* ---------- magnifier affordance ---------- */
  var MAG = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
            'stroke-width="2.2" stroke-linecap="round" aria-hidden="true">' +
            '<circle cx="11" cy="11" r="7"/><path d="M20 20l-3.6-3.6"/>' +
            '<path d="M11 8.2v5.6M8.2 11h5.6"/></svg>';

  var zoomBtn = null;

  function mountButton() {
    var m = mainImgEl();
    if (!m) return;
    var stage = m.closest('.main');
    if (!stage || stage.querySelector('.ke-zoom-btn')) return;
    zoomBtn = document.createElement('button');
    zoomBtn.type = 'button';
    zoomBtn.className = 'ke-zoom-btn';
    zoomBtn.setAttribute('aria-label', 'Open image zoom');
    zoomBtn.innerHTML = MAG;
    zoomBtn.addEventListener('click', function (e) { e.stopPropagation(); open(zoomBtn); });
    stage.appendChild(zoomBtn);
    m.addEventListener('click', function () { open(null); });
  }

  /* ---------- lightbox ---------- */
  var lb, lbImg, lbCount, lbHint, prevBtn, nextBtn, closeBtn;
  var list = [], idx = 0, opener = null;
  var scale = 1, tx = 0, ty = 0;
  var inerted = [];
  var loadToken = 0;

  function build() {
    lb = document.createElement('div');
    lb.className = 'ke-lb';
    lb.hidden = true;
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Product image viewer');
    lb.innerHTML =
      '<div class="ke-lb-stage">' +
        '<img class="ke-lb-img" alt="" tabindex="0">' +
      '</div>' +
      '<div class="ke-lb-hint">' +
        (COARSE ? 'Tap to zoom · pinch for more' : 'Click image to zoom') +
      '</div>' +
      '<button type="button" class="ke-lb-close" aria-label="Close image viewer">' +
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="2.2" stroke-linecap="round" aria-hidden="true">' +
        '<path d="M18 6L6 18M6 6l12 12"/></svg></button>' +
      '<button type="button" class="ke-lb-prev" aria-label="Previous image">' +
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M15 5l-7 7 7 7"/></svg></button>' +
      '<button type="button" class="ke-lb-next" aria-label="Next image">' +
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M9 5l7 7-7 7"/></svg></button>' +
      '<div class="ke-lb-count" aria-live="polite"></div>';
    document.body.appendChild(lb);

    lbImg = lb.querySelector('.ke-lb-img');
    lbCount = lb.querySelector('.ke-lb-count');
    lbHint = lb.querySelector('.ke-lb-hint');
    closeBtn = lb.querySelector('.ke-lb-close');
    prevBtn = lb.querySelector('.ke-lb-prev');
    nextBtn = lb.querySelector('.ke-lb-next');

    closeBtn.addEventListener('click', close);
    prevBtn.addEventListener('click', function (e) { e.stopPropagation(); go(-1); });
    nextBtn.addEventListener('click', function (e) { e.stopPropagation(); go(1); });

    lb.addEventListener('click', function (e) {
      if (e.target === lb || e.target.classList.contains('ke-lb-stage')) close();
    });

    /* mouse/pen click-to-zoom. Touch taps are handled natively in wireTouch;
       the synthesized click that follows a touch is swallowed here — it was
       causing both accidental un-zoom after a tiny pan and the double-tap
       zoom-in-then-out self-cancel. */
    lbImg.addEventListener('click', function (e) {
      e.stopPropagation();
      if (Date.now() - lastTouchEnd < 700) return;
      if (dragMoved) return;
      toggleZoom(e.clientX, e.clientY);
    });

    /* keyboard zoom on the focusable image */
    lbImg.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        var r = lbImg.getBoundingClientRect();
        toggleZoom(r.left + r.width / 2, r.top + r.height / 2);
      }
    });

    lbImg.addEventListener('load', function () { lb.classList.remove('is-loading'); });
    lbImg.addEventListener('error', function () {
      var t = loadToken;
      /* hi-res failed: fall back to the original catalog-size url once */
      if (lbImg.dataset.fallback !== String(t) && list[idx]) {
        lbImg.dataset.fallback = String(t);
        lbImg.src = list[idx].url;
        return;
      }
      lb.classList.remove('is-loading');
      if (lbHint) { lbHint.textContent = 'Image failed to load'; lbHint.style.opacity = 1; }
    });

    window.addEventListener('resize', function () {
      if (lb.hidden) return;
      clampPan(); apply();   // re-clamp after rotation / viewport change
    });

    wirePointer();
    wireWheel();
    wireTouch();
  }

  function apply() {
    lbImg.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
    lb.classList.toggle('is-zoomed', scale > 1.01);
  }
  function reset() { scale = 1; tx = 0; ty = 0; apply(); }

  /* offsetWidth/Height are LAYOUT sizes — immune to the CSS transform,
     unlike getBoundingClientRect (the v1 dead-centre-pin bug). */
  function clampPan() {
    var maxX = Math.max(0, (lbImg.offsetWidth * scale - window.innerWidth) / 2);
    var maxY = Math.max(0, (lbImg.offsetHeight * scale - window.innerHeight) / 2);
    tx = Math.min(maxX, Math.max(-maxX, tx));
    ty = Math.min(maxY, Math.max(-maxY, ty));
  }

  function zoomAt(cx, cy, next) {
    var r = lbImg.getBoundingClientRect();
    var ox = cx - (r.left + r.width / 2);
    var oy = cy - (r.top + r.height / 2);
    var k = next / scale;
    tx = tx - ox * (k - 1);
    ty = ty - oy * (k - 1);
    scale = next;
    clampPan();
    apply();
  }
  function toggleZoom(cx, cy) {
    if (scale > 1.01) reset();
    else zoomAt(cx, cy, ZOOM);
  }

  function show(i) {
    idx = (i + list.length) % list.length;
    var s = list[idx];
    loadToken++;
    delete lbImg.dataset.fallback;
    lb.classList.add('is-loading');
    if (lbHint) lbHint.style.opacity = '';
    lbImg.src = hires(s.url);
    lbImg.alt = s.alt;
    lbCount.textContent = list.length > 1 ? (idx + 1) + ' / ' + list.length : '';
    lb.dataset.single = list.length > 1 ? 'false' : 'true';
    reset();
  }
  function go(step) {
    if (list.length < 2) return;   // single image: arrows must not reset zoom
    show(idx + step);
  }

  function setInert(on) {
    try {
      if (on) {
        inerted = [];
        Array.prototype.forEach.call(document.body.children, function (el) {
          if (el === lb || el.tagName === 'SCRIPT' || el.hasAttribute('inert')) return;
          el.setAttribute('inert', '');
          inerted.push(el);
        });
      } else {
        inerted.forEach(function (el) { el.removeAttribute('inert'); });
        inerted = [];
      }
    } catch (e) { /* inert unsupported — aria-modal still applies */ }
  }

  function open(fromEl) {
    if (!lb) build();
    list = slides();
    if (!list.length) return;
    /* the main image is not focusable — restore focus to the magnifier */
    opener = (fromEl && fromEl.tabIndex >= 0) ? fromEl : (zoomBtn || document.body);
    purgeTouches();
    show(startIndex(list));
    lb.hidden = false;
    lockScroll(true);
    setInert(true);
    closeBtn.focus({ preventScroll: true });
    document.addEventListener('keydown', onKey, true);   // capture: never leaks to ke-pdp.js
  }
  function close() {
    if (!lb || lb.hidden) return;
    lb.hidden = true;
    lockScroll(false);
    setInert(false);
    document.removeEventListener('keydown', onKey, true);
    reset();
    purgeTouches();
    if (opener && document.body.contains(opener)) {
      try { opener.focus({ preventScroll: true }); } catch (e) { try { opener.focus(); } catch (e2) { /* ignore */ } }
    }
  }

  function lockScroll(on) {
    if (on) {
      var gutter = window.innerWidth - document.documentElement.clientWidth;
      if (gutter > 0) document.body.style.paddingRight = gutter + 'px';
      document.body.classList.add('ke-lb-open');
    } else {
      document.body.classList.remove('ke-lb-open');
      document.body.style.paddingRight = '';
    }
  }

  function onKey(e) {
    /* if some other overlay (Klaviyo popup, cookie banner) holds focus above
       us, let it own the keyboard */
    if (document.activeElement && document.activeElement !== document.body &&
        !lb.contains(document.activeElement)) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();          // ke-pdp.js's notify-panel Esc must not also fire
      close();
    } else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
    else if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      var r1 = lbImg.getBoundingClientRect();
      zoomAt(r1.left + r1.width / 2, r1.top + r1.height / 2, Math.min(MAXZ, scale * 1.4));
    } else if (e.key === '-' || e.key === '_') {
      e.preventDefault();
      var r2 = lbImg.getBoundingClientRect();
      var next = Math.max(1, scale / 1.4);
      if (next === 1) reset();
      else zoomAt(r2.left + r2.width / 2, r2.top + r2.height / 2, next);
    } else if (e.key === 'Tab') {
      var f = [closeBtn, prevBtn, nextBtn, lbImg].filter(function (b) { return b.offsetParent !== null; });
      if (!f.length) return;
      var i = f.indexOf(document.activeElement);
      e.preventDefault();
      f[(i + (e.shiftKey ? -1 : 1) + f.length) % f.length].focus();
    }
  }

  /* ---------- mouse / pen pan ---------- */
  var dragging = false, dragMoved = false, sx = 0, sy = 0, stx = 0, sty = 0;
  function wirePointer() {
    lbImg.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' || scale <= 1.01) return;
      dragging = true; dragMoved = false;
      sx = e.clientX; sy = e.clientY; stx = tx; sty = ty;
      lb.classList.add('is-panning');
      try { lbImg.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    });
    lbImg.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - sx, dy = e.clientY - sy;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragMoved = true;
      tx = stx + dx; ty = sty + dy;
      clampPan(); apply();
    });
    ['pointerup', 'pointercancel'].forEach(function (t) {
      lbImg.addEventListener(t, function (e) {
        if (e.pointerType === 'touch') return;
        dragging = false;
        lb.classList.remove('is-panning');
        setTimeout(function () { dragMoved = false; }, 0);
      });
    });
  }

  /* ---------- wheel / trackpad zoom ---------- */
  function wireWheel() {
    lb.addEventListener('wheel', function (e) {
      e.preventDefault();
      var next = Math.min(MAXZ, Math.max(1, scale * (e.deltaY < 0 ? 1.12 : 1 / 1.12)));
      if (next === 1) reset(); else zoomAt(e.clientX, e.clientY, next);
    }, { passive: false });
  }

  /* ---------- touch state machine ----------
     Modes are derived from the live touch count on EVERY transition:
       0->1  arm tap + (pan | swipe) baselines
       1->2  arm pinch from CURRENT positions (kills tap)
       2->1  RE-ARM the pan baseline from the surviving finger + current
             tx/ty (the v1 teleport bug), swipe stays disarmed
       3->2  re-arm pinch from the actual remaining pair
     touchcancel is handled exactly like touchend, and pts is purged on
     open/close, so no ghost touches survive OS interruptions. */
  var pts = {};
  var lastTouchEnd = 0, lastTapAt = 0;
  var pinch = { armed: false, startDist: 0, startScale: 1, midX: 0, midY: 0, startTx: 0, startTy: 0 };
  var one = { armed: false, x: 0, y: 0, stx: 0, sty: 0, swipe: false, moved: false, t0: 0 };

  function purgeTouches() {
    pts = {};
    pinch.armed = false;
    one.armed = false;
  }
  function activeTouches() { return Object.keys(pts); }
  function dist() {
    var k = activeTouches();
    if (k.length < 2) return 0;
    var a = pts[k[0]], b = pts[k[1]];
    return Math.hypot(a.x - b.x, a.y - b.y);
  }
  function mid() {
    var k = activeTouches(), a = pts[k[0]], b = pts[k[1]];
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }
  function armOne(allowSwipe) {
    var k = activeTouches();
    if (k.length !== 1) return;
    var p = pts[k[0]];
    one.armed = true;
    one.x = p.x; one.y = p.y;
    one.stx = tx; one.sty = ty;
    one.swipe = allowSwipe && scale <= 1.01;
    one.moved = false;
    one.t0 = Date.now();
    pinch.armed = false;
  }
  function armPinch() {
    if (activeTouches().length < 2) return;
    var m = mid();
    pinch.armed = true;
    pinch.startDist = dist() || 1;   // never divide by zero
    pinch.startScale = scale;
    pinch.midX = m.x; pinch.midY = m.y;
    pinch.startTx = tx; pinch.startTy = ty;
    one.armed = false;
  }

  function onTouchEndLike(e) {
    var enderIds = [];
    for (var i = 0; i < e.changedTouches.length; i++) enderIds.push(e.changedTouches[i].identifier);
    var wasTap = one.armed && !one.moved && activeTouches().length === 1 &&
                 (Date.now() - one.t0) < 450;
    var tapX = 0, tapY = 0;
    if (wasTap) {
      var k0 = activeTouches()[0];
      tapX = pts[k0].x; tapY = pts[k0].y;
    }
    enderIds.forEach(function (id) { delete pts[id]; });
    lastTouchEnd = Date.now();

    var n = activeTouches().length;
    if (n === 0) {
      if (scale < 1.02) reset();
      if (wasTap && e.target === lbImg) {
        var now = Date.now();
        if (now - lastTapAt > TAP_DEBOUNCE) {   // double-tap acts once
          lastTapAt = now;
          toggleZoom(tapX, tapY);
        }
      } else if (one.armed && one.swipe && one.moved && list.length > 1) {
        var lastId = enderIds[enderIds.length - 1];
        var end = lastEnd[lastId];
        if (end) {
          var dx = end.x - one.x, dy = end.y - one.y;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) go(dx < 0 ? 1 : -1);
        }
      }
      one.armed = false;
      pinch.armed = false;
    } else if (n === 1) {
      armOne(false);        // pinch collapsed to one finger: pan re-baselined, swipe off
    } else if (n === 2) {
      armPinch();           // 3->2: rebuild pinch from the real remaining pair
    }
  }

  var lastEnd = {};

  function wireTouch() {
    lb.addEventListener('touchstart', function (e) {
      for (var i = 0; i < e.changedTouches.length; i++) {
        var t = e.changedTouches[i];
        pts[t.identifier] = { x: t.clientX, y: t.clientY };
      }
      var n = activeTouches().length;
      if (n === 1) armOne(true);
      else if (n >= 2) armPinch();
    }, { passive: true });

    lb.addEventListener('touchmove', function (e) {
      for (var i = 0; i < e.changedTouches.length; i++) {
        var t = e.changedTouches[i];
        if (pts[t.identifier]) { pts[t.identifier].x = t.clientX; pts[t.identifier].y = t.clientY; }
      }
      var n = activeTouches().length;
      if (n >= 2 && pinch.armed) {
        e.preventDefault();
        var m = mid();
        var next = Math.min(MAXZ, Math.max(1, pinch.startScale * (dist() / pinch.startDist)));
        /* two-finger drag pans too: carry the mid-point movement */
        tx = pinch.startTx + (m.x - pinch.midX);
        ty = pinch.startTy + (m.y - pinch.midY);
        zoomAt(m.x, m.y, next);
      } else if (n === 1 && one.armed) {
        var k = activeTouches()[0], p = pts[k];
        var dx = p.x - one.x, dy = p.y - one.y;
        if (Math.abs(dx) > TAP_SLOP || Math.abs(dy) > TAP_SLOP) one.moved = true;
        if (scale > 1.01) {
          e.preventDefault();
          tx = one.stx + dx; ty = one.sty + dy;
          clampPan(); apply();
        }
      }
    }, { passive: false });

    ['touchend', 'touchcancel'].forEach(function (t) {
      lb.addEventListener(t, function (e) {
        for (var i = 0; i < e.changedTouches.length; i++) {
          var ct = e.changedTouches[i];
          lastEnd[ct.identifier] = { x: ct.clientX, y: ct.clientY };
        }
        onTouchEndLike(e);
      }, { passive: true });
    });
  }

  /* ---------- boot (+ theme-editor section reloads) ---------- */
  function boot() { mountButton(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function () {
    zoomBtn = null;
    boot();
  });
})();
